/**
 * Vignettes des familles de diagrammes.
 *
 * Choisir entre « camembert » et « barres empilées » sur la foi de deux mots est
 * un exercice d'imagination ; le même choix devant deux dessins prend une seconde.
 * D'où ces miniatures : elles ne montrent pas le résultat — celui-ci dépend des
 * données — mais la **forme** de ce qu'on demande.
 *
 * Dessinées en SVG dans le code, sans aucun fichier ni aucune police externe :
 * l'interface du portail est servie derrière une politique de sécurité stricte qui
 * n'autorise pas d'image distante, et une vignette qui ne s'affiche pas vaut moins
 * qu'un libellé seul.
 *
 * Les couleurs viennent de `currentColor` et d'une seule teinte d'accent, pour que
 * les vignettes suivent le thème clair ou sombre sans être redessinées.
 */

import type { ChartKind } from '../lib/guided-description';

/** Boîte de dessin commune : toutes les vignettes partagent ce repère. */
const VIEW = { width: 64, height: 40 } as const;

/** Traits et remplissages, exprimés en classes pour suivre le thème. */
const ACCENT = 'fill-indigo-500 dark:fill-indigo-400';
const ACCENT_PALE = 'fill-indigo-300 dark:fill-indigo-700';
const TRAIT = 'stroke-indigo-500 dark:stroke-indigo-400';
const AXE = 'stroke-slate-300 dark:stroke-slate-600';

/** Axes en L, communs aux diagrammes cartésiens. */
function Axes() {
  return (
    <path
      d={`M6 4 V34 H${VIEW.width - 4}`}
      className={AXE}
      strokeWidth="1.5"
      fill="none"
      strokeLinecap="round"
    />
  );
}

const DESSINS: Record<ChartKind, () => JSX.Element> = {
  kpi: () => (
    <>
      {[0, 1, 2].map((index) => (
        <g key={index}>
          <rect
            x={4 + index * 19}
            y={8}
            width={16}
            height={24}
            rx={2}
            className="fill-slate-100 dark:fill-slate-800"
          />
          <rect x={7 + index * 19} y={12} width={7} height={2} rx={1} className={ACCENT_PALE} />
          <rect x={7 + index * 19} y={17} width={10} height={6} rx={1} className={ACCENT} />
        </g>
      ))}
    </>
  ),

  bar: () => (
    <>
      <Axes />
      {[14, 22, 10, 26, 18].map((hauteur, index) => (
        <rect
          key={index}
          x={10 + index * 10}
          y={34 - hauteur}
          width={6}
          height={hauteur}
          rx={1}
          className={ACCENT}
        />
      ))}
    </>
  ),

  line: () => (
    <>
      <Axes />
      <path
        d="M9 28 L21 20 L33 24 L45 10 L57 14"
        className={TRAIT}
        strokeWidth="2"
        fill="none"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      {[
        [9, 28],
        [21, 20],
        [33, 24],
        [45, 10],
        [57, 14],
      ].map(([cx, cy]) => (
        <circle key={`${cx}`} cx={cx} cy={cy} r={2} className={ACCENT} />
      ))}
    </>
  ),

  pie: () => (
    <>
      {/* Trois parts : un tour complet découpé, ce qui est exactement le propos. */}
      <circle cx={32} cy={20} r={15} className={ACCENT_PALE} />
      <path d="M32 20 L32 5 A15 15 0 0 1 45.9 14.2 Z" className={ACCENT} />
      <path
        d="M32 20 L45.9 14.2 A15 15 0 0 1 39 33.4 Z"
        className="fill-indigo-700 dark:fill-indigo-300"
      />
    </>
  ),

  stacked: () => (
    <>
      <Axes />
      {[
        [10, 8],
        [14, 10],
        [8, 6],
        [16, 9],
      ].map(([bas, haut], index) => (
        <g key={index}>
          <rect
            x={12 + index * 12}
            y={34 - bas!}
            width={7}
            height={bas!}
            rx={1}
            className={ACCENT}
          />
          <rect
            x={12 + index * 12}
            y={34 - bas! - haut!}
            width={7}
            height={haut!}
            rx={1}
            className={ACCENT_PALE}
          />
        </g>
      ))}
    </>
  ),

  gauge: () => (
    <>
      {/* Demi-arc : la valeur parcourue, puis le reste à parcourir. */}
      <path
        d="M8 32 A24 24 0 0 1 56 32"
        className="stroke-slate-200 dark:stroke-slate-700"
        strokeWidth="6"
        fill="none"
        strokeLinecap="round"
      />
      <path
        d="M8 32 A24 24 0 0 1 44 11.5"
        className={TRAIT}
        strokeWidth="6"
        fill="none"
        strokeLinecap="round"
      />
      <circle cx={32} cy={32} r={2.5} className="fill-slate-500 dark:fill-slate-400" />
    </>
  ),

  table: () => (
    <>
      <rect x={5} y={7} width={54} height={7} rx={1.5} className={ACCENT} />
      {[17, 24, 31].map((y) => (
        <g key={y}>
          <rect
            x={5}
            y={y}
            width={16}
            height={5}
            rx={1}
            className="fill-slate-200 dark:fill-slate-700"
          />
          <rect
            x={24}
            y={y}
            width={14}
            height={5}
            rx={1}
            className="fill-slate-200 dark:fill-slate-700"
          />
          <rect
            x={41}
            y={y}
            width={18}
            height={5}
            rx={1}
            className="fill-slate-200 dark:fill-slate-700"
          />
        </g>
      ))}
    </>
  ),

  scatter: () => (
    <>
      <Axes />
      {[
        [13, 29],
        [19, 24],
        [24, 26],
        [30, 19],
        [36, 21],
        [42, 14],
        [48, 16],
        [54, 9],
      ].map(([cx, cy]) => (
        <circle key={`${cx}-${cy}`} cx={cx} cy={cy} r={2.2} className={ACCENT} />
      ))}
    </>
  ),
};

export interface ChartThumbnailProps {
  kind: ChartKind;
  className?: string;
}

/**
 * Miniature d'une famille de diagrammes.
 *
 * `aria-hidden` : le libellé et l'explication qui l'accompagnent disent déjà tout.
 * Décrire le dessin en plus produirait une redite à chaque option de la liste.
 */
export function ChartThumbnail({ kind, className }: ChartThumbnailProps) {
  const Dessin = DESSINS[kind];
  return (
    <svg
      viewBox={`0 0 ${VIEW.width} ${VIEW.height}`}
      className={className}
      aria-hidden="true"
      focusable="false"
    >
      <Dessin />
    </svg>
  );
}
